import { useState, type ReactNode } from 'react'
import { capabilityAct, shortDate, signersText, type GateStage } from '../lib/api'
import { personLabel, usePeopleNames } from '../lib/people'
import { stageSignFooter } from './footers'
import { DialogFooter, StageList, Status, useInDialog, type ListStage, type StageState } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/sweep.css'

// A chain of signatures under the approval matrix, drawn as the kit's stage
// list: the activation gate on a vendor and the chain on one risk acceptance.
//
// # WHY ONE COMPONENT FOR BOTH
//
// The two chains are the same shape from the same API (GateStage), signed the
// same way, and used to be drawn by two copies of one row that each recited the
// capability code, the reason, who held it, the order rule and the refusal as
// five paragraphs per stage. One stage now reads as one line: what the stage
// does, one status, who signed or who may, and the reason behind the (i).
//
// # WHAT THE CALLER DECIDES
//
// Which stage takes a signature, which is only shown, and why this reader may
// not sign are the caller's walls, tested in the order the API tests them. The
// component draws the answer: the Sign button stays greyed with the refusal
// behind the (i) beside it and in its title, so the reason is read before the
// press rather than after it.

type Props = {
  stages: GateStage[]
  // The stage this reader may act on now.
  actionable: (s: GateStage) => boolean
  // The stage that would be next where the chain is shown but not in force.
  offered?: (s: GateStage) => ReactNode
  // Why this reader may not sign, or '' when they may.
  refusal: (s: GateStage) => string
  // What the note is kept for, in a few words.
  noteLabel: string
  busy: boolean
  onSign: (stageIndex: number, note: string) => void
  // The persona spec id for the Sign button, section 9.1's test hooks. This
  // component is shared by two different chains (the exception chain and the
  // vendor activation gate) which are two different spec actions on the same
  // call shape, so the caller names which one it is.
  actionId: string
}

export function SignatureStages({ stages, actionable, offered, refusal, noteLabel, busy, onSign, actionId }: Props) {
  const [note, setNote] = useState('')
  const names = usePeopleNames()
  // In a dialog (the chain on one risk acceptance) signing is the dialog's
  // main action and is drawn in its footer; on a page it stays under the note.
  const inDialog = useInDialog()

  const list: ListStage[] = stages.map((s) => {
    const state: StageState = s.signed ? 'done' : s.next ? 'current' : 'upcoming'
    const holders = signersText(s.heldBy)
    const why = refusal(s)
    const noteId = 'sig-note-' + s.index
    const reasonId = 'sig-reason-' + s.index

    let detail: ReactNode
    if (actionable(s)) {
      detail = (
        <div className="act-sign">
          <label htmlFor={noteId}>{noteLabel}</label>
          <textarea
            id={noteId}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy || why !== ''}
            aria-describedby={why && !inDialog ? reasonId : undefined}
            style={{ width: '100%', marginTop: 4 }}
          />
          {inDialog ? (
            <DialogFooter
              {...stageSignFooter({ index: s.index, note, why, busy, actionId }, onSign, () => setNote(''))}
              note={why ? <ReasonTip label="Why you cannot sign this stage" text={why} /> : undefined}
            />
          ) : (
            <div className="sw-row" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="primary"
                disabled={busy || why !== ''}
                title={why || undefined}
                aria-describedby={why ? reasonId : undefined}
                data-action={actionId}
                onClick={() => {
                  onSign(s.index, note)
                  setNote('')
                }}
              >
                {busy ? 'Recording' : 'Sign this stage'}
              </button>
              {why && (
                <span className="muted sw-row" id={reasonId}>
                  Not yours to sign <ReasonTip label="Why you cannot sign this stage" text={why} />
                </span>
              )}
            </div>
          )}
        </div>
      )
    } else if (offered) {
      detail = offered(s)
    }

    // The act is a phrase the API also uses mid-sentence ("the ability to
    // approve a vendor"), so it arrives in lower case and is capitalised here,
    // where it stands as the stage's name.
    const act = capabilityAct(s.capability)
    return {
      key: String(s.index),
      // The matrix's own name for the stage ("TPRM approval"), or the act where
      // it gave none.
      label: s.name || act.charAt(0).toUpperCase() + act.slice(1),
      state,
      person: s.signed ? personLabel(names, s.signedBy) : undefined,
      when: s.signed && s.signedAt ? shortDate(s.signedAt) : undefined,
      // A signer's note is theirs and is shown as they wrote it.
      sub: s.signed ? s.note || undefined : holders ? 'Held by ' + holders : 'No role holds this, so nobody can sign',
      tip: s.why.length <= 200 ? s.why : undefined,
      blocker: s.superseded ? (
        <>
          <Status tone="warn">Needs signing again</Status>
          <ReasonTip label="Why this stage needs signing again" text={s.superseded} />
        </>
      ) : undefined,
      // A reason too long for a toggletip opens the same (i) as a drawer.
      detail:
        s.why.length > 200 || detail ? (
          <>
            {s.why.length > 200 && <ReasonTip label={'Why ' + capabilityAct(s.capability)} text={s.why} />}
            {detail}
          </>
        ) : undefined,
    }
  })

  return <StageList stages={list} />
}
